import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Poll the unread count so the badge stays fresh without a websocket.
// Every 60s is plenty — this isn't a chat app.
const POLL_MS = 60_000;

/**
 * NotificationBell — a small round bell chip with an unread badge.
 * Renders only when the user is signed in. Links to /notifications.
 */
const NotificationBell = ({ tone = 'dark' }) => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('rbr_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const r = await fetch(`${API_URL}/api/notifications/unread-count`, {
        headers: authHeaders(),
      });
      if (r.ok) {
        const b = await r.json();
        setCount(b.unreadCount || 0);
      }
    } catch { /* silent */ }
  }, [user, authHeaders]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [poll]);

  if (!user) return null;

  // `tone='dark'` for use on the dark app header; `light` for cream sections.
  const isDark = tone === 'dark';
  return (
    <Link
      to="/notifications"
      data-testid="notification-bell"
      aria-label={count > 0 ? `${count} unread notifications` : 'Notifications'}
      className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors active:scale-95 ${
        isDark
          ? 'bg-white/10 border border-white/20 hover:bg-white/20 text-white'
          : 'bg-white border border-stone-200 hover:bg-stone-50 text-stone-700'
      }`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <Bell className="w-4 h-4" />
      {count > 0 && (
        <span
          className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold text-white bg-orange-500 border-2"
          style={{ borderColor: isDark ? '#1a2f2a' : '#FFFCF3' }}
          data-testid="notification-bell-badge"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
};

export default NotificationBell;
